// The top-level sections of the app: ONE list.
//
// Before this file, App.jsx's <Route>s and AppLayout's NAV_ITEMS were two
// hardcoded lists that nothing kept in sync — adding a section meant remembering
// both, and forgetting one produced either a nav link to NotFound or a page
// nobody can navigate to. Routes cannot be generated from here (they carry
// elements, and /tickets/new and /tickets/:publicRef are routes without sections),
// so the sync is enforced the other way round: App.jsx declares its route paths
// and calls `assertRoutesCoverSections` at module scope, which throws at import
// time — i.e. at boot, in the browser console, loudly — if a section has no route.

import { atLeastRole } from '../session/roles.js';

/**
 * `requires: null` means every authenticated user; otherwise `{ product, role }`
 * is the minimum rank, checked with the same ranks the server uses.
 *
 * Admin requires support/lead, matching `requireProductRole('support', 'lead')`
 * on the server router. Drift between the two shows a link whose every call 403s.
 *
 * The same `{ product, role }` shape is what a FEATURE inside the console will
 * carry — the developer's spec gates features on lead vs admin separately — so a
 * section and a descriptor can be asked the same question by `atLeastRole` without
 * this list learning a second vocabulary.
 */
export const SECTIONS = [
  { to: '/tickets',   label: 'Tickets',   requires: null },
  { to: '/customers', label: 'Customers', requires: null },
  { to: '/reports',   label: 'Reports',   requires: null },
  { to: '/admin',     label: 'Admin',     requires: { product: 'support', role: 'lead' } },
];

/**
 * The sections to render for this user. Presentation only — the API refuses the
 * rest regardless.
 */
export function visibleSections(user) {
  return SECTIONS.filter(
    (section) => section.requires === null
      || atLeastRole(user, section.requires.product, section.requires.role),
  );
}

/**
 * Throws if any section has no route. Called at module scope in App.jsx so the
 * failure lands at import time rather than when somebody clicks the link.
 *
 * @param {string[]} routePaths  Every `path` App.jsx renders.
 * @param {typeof SECTIONS} sections
 */
export function assertRoutesCoverSections(routePaths, sections = SECTIONS) {
  const paths = new Set(routePaths);
  const missing = sections.filter((section) => !paths.has(section.to));
  if (missing.length > 0) {
    throw new Error(
      `Nav sections with no route: ${missing.map((s) => s.to).join(', ')}. `
      + 'Add the <Route> in App.jsx, or remove the section from nav-items.js.',
    );
  }
}
